import { ChangeEvent, useState } from 'react';

import { FormControl, Grid, InputLabel, Paper, Select, makeStyles } from '@material-ui/core';

import { useAdminGetCategories } from '../../hooks/admin-get-categories.hook';
import { useUpdateCategory } from '../../hooks/update-category.hook';
import { useUpdateCategoriesPositions } from '../../hooks/update-categories-positions.hook';
import { useArchiveCategory } from '../../hooks/archive-category.hook';
import { useRestoreCategory } from '../../hooks/restore-category.hook';
import { Category } from '../../types/category.type';
import { UpdateCategoryRequestDTO } from '../../types/dtos/update-category.dto';
import {
  ENTITY_STATUS_FILTERS,
  ENTITY_STATUS_FILTER_LABELS,
  EntityStatusFilter,
  toEntityListFilters,
} from '../../../shared/constants/entity-status-filter.constants';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';
import { ConfirmActionDialog } from '../../../shared/components/Modals/ConfirmActionDialog.component';
import CategoriesTable from '../../components/Tables/CategoriesTable.component';
import CreateCategoryForm from '../../components/Forms/CreateCategoryForm.component';
import { EditCategoryDialog } from '../../components/Modals/EditCategoryDialog.component';

const STATUS_FILTER_INPUT_ID = 'categories-status-filter';

type ArchiveAction = 'archive' | 'restore';

type DialogState =
  | { kind: 'none' }
  | { kind: 'edit'; category: Category }
  | { kind: 'confirm'; action: ArchiveAction; category: Category };

type ConfirmDialogContent = {
  title: string;
  describe: (categoryName: string) => string;
  confirmLabel: string;
};

const CONFIRM_DIALOG_CONTENT: Record<ArchiveAction, ConfirmDialogContent> = {
  archive: {
    title: 'Archiver la rubrique',
    describe: (categoryName) =>
      `La rubrique « ${categoryName} » sera retirée des listes et du site, et pourra être restaurée.`,
    confirmLabel: 'Archiver',
  },
  restore: {
    title: 'Restaurer la rubrique',
    describe: (categoryName) => `La rubrique « ${categoryName} » retrouvera sa place en fin de liste, inactive.`,
    confirmLabel: 'Restaurer',
  },
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },

    '& h4': {
      margin: '0 0 0.5rem 0',
      opacity: 0.7,
    },
  },

  listColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
  },

  createPanel: {
    padding: '1rem 0.75rem',
  },

  filters: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
  },
});

export default function AdminCategoriesListView() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [statusFilter, setStatusFilter] = useState<EntityStatusFilter>('all');
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const { isLoading, isRefetching, categories, totalCount, refetch } = useAdminGetCategories({
    page: page + 1,
    limit: rowsPerPage,
    sortBy: 'position',
    sortDirection: 'asc',
    filters: toEntityListFilters(statusFilter),
  });

  const { updatePositions } = useUpdateCategoriesPositions();
  const { updateCategory, isSubmitting: isUpdating } = useUpdateCategory();
  const { archiveCategory, isSubmitting: isArchiving } = useArchiveCategory();
  const { restoreCategory, isSubmitting: isRestoring } = useRestoreCategory();

  const isDragEnabled = statusFilter === 'all';

  const closeDialog = () => setDialog({ kind: 'none' });

  const handlePositionsChange = async (positions: { id: string; position: number }[]) => {
    await updatePositions(positions);
  };

  const handleToggleActive = async (categoryId: string, isActive: boolean): Promise<boolean> => {
    const result = await updateCategory({ categoryId, categoryData: { isActive } });
    return result !== null;
  };

  const handleEditSubmit = async (categoryData: UpdateCategoryRequestDTO) => {
    if (dialog.kind !== 'edit') return;

    const updatedCategory = await updateCategory({ categoryId: dialog.category.id, categoryData });
    if (!updatedCategory) return;

    closeDialog();
    await refetch();
  };

  const handleConfirm = async () => {
    if (dialog.kind !== 'confirm') return;

    const { action, category } = dialog;
    const result = action === 'archive' ? await archiveCategory(category.id) : await restoreCategory(category.id);
    if (!result) return;

    closeDialog();
    await refetch();
  };

  const handleStatusFilterChange = (value: EntityStatusFilter) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <section className={classes.root}>
      <h2>Rubriques</h2>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8} className={classes.listColumn}>
          <section className={classes.filters}>
            <FormControl variant="outlined" size="small">
              <InputLabel htmlFor={STATUS_FILTER_INPUT_ID}>Statut</InputLabel>
              <Select
                native
                label="Statut"
                value={statusFilter}
                inputProps={{ id: STATUS_FILTER_INPUT_ID }}
                onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                  handleStatusFilterChange(event.target.value as EntityStatusFilter)
                }
              >
                {ENTITY_STATUS_FILTERS.map((status) => (
                  <option key={status} value={status}>
                    {ENTITY_STATUS_FILTER_LABELS[status]}
                  </option>
                ))}
              </Select>
            </FormControl>
          </section>
          <CategoriesTable
            categories={categories}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            isLoading={isLoading || isRefetching}
            isDragEnabled={isDragEnabled}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onPositionsChange={handlePositionsChange}
            onToggleActive={handleToggleActive}
            onEdit={(category) => setDialog({ kind: 'edit', category })}
            onArchive={(category) => setDialog({ kind: 'confirm', action: 'archive', category })}
            onRestore={(category) => setDialog({ kind: 'confirm', action: 'restore', category })}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <h4>Créer une rubrique</h4>
          <Paper className={classes.createPanel}>
            <CreateCategoryForm onCategoryCreated={refetch} />
          </Paper>
        </Grid>
      </Grid>
      {dialog.kind === 'edit' && (
        <EditCategoryDialog
          open
          category={dialog.category}
          isSubmitting={isUpdating}
          onSubmit={handleEditSubmit}
          onClose={closeDialog}
        />
      )}
      {dialog.kind === 'confirm' && (
        <ConfirmActionDialog
          open
          title={CONFIRM_DIALOG_CONTENT[dialog.action].title}
          description={CONFIRM_DIALOG_CONTENT[dialog.action].describe(dialog.category.name)}
          confirmLabel={CONFIRM_DIALOG_CONTENT[dialog.action].confirmLabel}
          isSubmitting={isArchiving || isRestoring}
          onConfirm={handleConfirm}
          onClose={closeDialog}
        />
      )}
    </section>
  );
}
